import styled from "styled-components";
import { Mail } from 'lucide-react';
import * as TypographyStyles from '../TypographyStyles';
import { slateColors, purpleColors, fontFamily } from '../ContentPages/AppLandingPage/styles';
import { useTranslation } from 'react-i18next';
import { JourneyWithHafas } from '../../utils/mapJourneysData';
import { getLineName } from '../../utils/mapJourneysData';

const Container = styled('div')`
    display: flex;
    padding: 1.5rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 1.5rem;
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    background: white;
    border-radius: 2.5rem;
    border: 1px solid ${slateColors[200]};
    box-shadow: 0 2px 6px -2px rgba(15, 23, 42, 0.12);
    transition: all 0.3s;
    
    &:hover {
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        transform: translateY(-2px);
    }
    
    @media (min-width: 768px) {
        width: min(100%, 400px);
    }
`

const CardBody = styled('div')`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
`

const FeedbackButton = styled.a`
    text-decoration: none;
    width: 100%;
`;

const PurpleButton = styled.button`
    font-family: ${fontFamily};
    font-weight: 600;
    font-size: 1rem;
    display: inline-flex;
    padding: 0.75rem 1rem;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
    border-radius: 0.75rem;
    border: 1px solid ${slateColors[300]};
    background: ${slateColors[50]};
    color: ${purpleColors[600]};
    cursor: pointer;
    transition: all 0.2s;
    width: 100%;
    -webkit-tap-highlight-color: transparent;

    &:hover {
        background: white;
        border-color: ${slateColors[400]};
        color: ${purpleColors[700]};
        transform: translateY(-1px);
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    }

    &:active {
        transform: translateY(0);
        background: ${slateColors[100]};
    }

    &:focus {
        outline: none;
        border-color: ${purpleColors[400]};
        box-shadow: 0 0 0 3px rgba(147, 51, 234, 0.15);
    }

    svg {
        width: 1rem;
        height: 1rem;
        flex-shrink: 0;
    }
`;

interface FeedbackCardProps {
    source: string;
    journey?: JourneyWithHafas;
    journeys?: JourneyWithHafas[];
    searchParams?: {
        originName: string;
        destinationName: string;
        date: string;
        tripType: string;
    };
}

const FeedbackCard: React.FC<FeedbackCardProps> = ({ source, journey, journeys, searchParams }) => {
  const { t } = useTranslation();
  
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    } catch {
      return dateString;
    }
  };

  let emailBody = t('feedbackCard.emailBody');
  
  // Add journey details if single journey is provided (journey details screen)
  // This should be exclusive - only show journey details OR journey list summary, not both
  if (journey) {
    emailBody += `


---
Journey ID: ${journey.id}
${t('feedbackCard.emailPlatform')}

Journey Details:`;
    
    journey.legs.forEach((leg, index) => {
      emailBody += `


Leg ${index + 1}:
Line: ${getLineName(leg)}
From: ${leg.origin.name}
To: ${leg.destination.name}
Planned Departure: ${formatDate(leg.plannedDeparture)}
Planned Arrival: ${formatDate(leg.plannedArrival)}`;
    });
  } else if (journeys && journeys.length > 0 && searchParams) {
    // Add search parameters and journey summary ONLY if journeys array is provided (journey list screen)
    // This is exclusive from the single journey details above
    emailBody += `


---
${t('feedbackCard.emailPlatform')}

Search Parameters:
Origin: ${searchParams.originName}
Destination: ${searchParams.destinationName}
Date: ${formatDate(searchParams.date)}
Trip Type: ${searchParams.tripType}

Displayed Journeys (${journeys.length}):`;
    
    journeys.forEach((journey, index) => {
      const firstLeg = journey.legs[0];
      const lastLeg = journey.legs[journey.legs.length - 1];
      const reliabilityScore = journey.reliability?.reliabilityScore ?? null;
      
      // Get line names from all legs (excluding walking legs if any)
      const lineNames = journey.legs
        .filter(leg => !leg.walking)
        .map(leg => getLineName(leg))
        .join(', ');
      
      emailBody += `


Journey ${index + 1}:
Journey ID: ${journey.id}
Line(s): ${lineNames}
Departure: ${formatDate(firstLeg.plannedDeparture)} from ${firstLeg.origin.name}
Arrival: ${formatDate(lastLeg.plannedArrival)} at ${lastLeg.destination.name}${reliabilityScore !== null ? `\nReliability Score: ${reliabilityScore.toFixed(1)}%` : ''}`;
    });
  }
  
  const mailtoLink = `mailto:feedback@chuuchuu.com?subject=${encodeURIComponent(source)}&body=${encodeURIComponent(emailBody)}`;

  return (
    <Container>
        <CardBody>
            <TypographyStyles.PageSubtitleSemiBold color={slateColors[900]}>{t('feedbackCard.betaMessage')}</TypographyStyles.PageSubtitleSemiBold>
            <TypographyStyles.BodyLRegular color={slateColors[600]}>{t('feedbackCard.description')}</TypographyStyles.BodyLRegular>
        </CardBody>
        <FeedbackButton href={mailtoLink}>
            <PurpleButton>
                <Mail aria-hidden />
                {t('feedbackCard.button')}
            </PurpleButton>
        </FeedbackButton>
    </Container>
  )
}

export default FeedbackCard;
